import { createSelector } from '@reduxjs/toolkit';
import type { ExchangesState } from '@skynet/data-shared-exchanges';
import { selectExchangesList } from '@skynet/data-shared-exchanges';
import type { SubAccountsState } from './types';
import { buildAccountRows, countEntityRows } from './transformations';

// Raw selectors
export interface SubAccountsStore {
  subAccounts: SubAccountsState;
  exchanges: ExchangesState;
}

const selectExchanges = (state: SubAccountsStore) => selectExchangesList(state.exchanges);

const selectSubAccountsSlice = (state: SubAccountsStore) => state.subAccounts;

export const selectMainAccounts = (state: SubAccountsStore) =>
  selectSubAccountsSlice(state).mainAccounts;
/** Loaded sub-accounts, by main account id. */
export const selectSubAccounts = (state: SubAccountsStore) =>
  selectSubAccountsSlice(state).subAccounts;
/** Progress of each main account's `SubAccounts` request, by its id. */
export const selectSubAccountsStatus = (state: SubAccountsStore) =>
  selectSubAccountsSlice(state).subAccountsStatus;

export const selectFilters = (state: SubAccountsStore) => selectSubAccountsSlice(state).filters;
export const selectSearch = (state: SubAccountsStore) => selectFilters(state).search;
export const selectExchangeFilter = (state: SubAccountsStore) => selectFilters(state).exchanges;
/** The `SupportedOperations` frame, by exchange id; see `SubAccountsState.supportedOperations`. */
export const selectSupportedOperations = (state: SubAccountsStore) =>
  selectSubAccountsSlice(state).supportedOperations;
export const selectError = (state: SubAccountsStore) => selectSubAccountsSlice(state).error;
/**
 * No accounts to show yet: the Listen is in flight, or has not gone out at all —
 * `useListen` debounces it, so the page renders a while before it is sent.
 */
export const selectIsLoading = (state: SubAccountsStore) => {
  const { requestStatus } = selectSubAccountsSlice(state);
  return requestStatus === null || requestStatus === 'pending';
};

export const selectRemoveStatus = (state: SubAccountsStore) =>
  selectSubAccountsSlice(state).resRemove;

/** Exchanges that have at least one main account — the options of the exchange filter. */
export const selectAccountExchanges = createSelector(
  selectExchanges,
  selectMainAccounts,
  (exchanges, mainAccounts) => {
    const ids = new Set(mainAccounts.map((a) => a.exchangeID));
    return exchanges.filter((e) => ids.has(e.id));
  },
);

/** List rows: one tree per main account (memoized). */
export const selectAccountRows = createSelector(
  selectExchanges,
  selectMainAccounts,
  selectSubAccounts,
  selectSubAccountsStatus,
  selectFilters,
  (exchanges, mainAccounts, subAccounts, subAccountsStatus, filters) =>
    buildAccountRows({ exchanges, mainAccounts, subAccounts, subAccountsStatus, filters }),
);

/** Accounts, sub-accounts and keys currently shown. */
export const selectFoundRecords = createSelector(selectAccountRows, countEntityRows);

/**
 * Accounts, sub-accounts and keys shown with no filter applied. Sub-accounts
 * count once loaded: they are not known before.
 */
export const selectTotalRecords = createSelector(
  selectExchanges,
  selectMainAccounts,
  selectSubAccounts,
  selectSubAccountsStatus,
  (exchanges, mainAccounts, subAccounts, subAccountsStatus) =>
    countEntityRows(
      buildAccountRows({
        exchanges,
        mainAccounts,
        subAccounts,
        subAccountsStatus,
        filters: { search: '', exchanges: [] },
      }),
    ),
);

/** True when a search or an exchange filter narrows the tree. */
export const selectHasActiveFilters = createSelector(
  selectFilters,
  (filters) => filters.search.trim() !== '' || filters.exchanges.length > 0,
);
